// Double-clicking a column edge makes the column as wide as its widest cell on screen,
// the header included, the way a spreadsheet does (Camiel, 21-09-2026: "hij doet het niet
// op de langste rij" — it used to go back to the column's standard width).

/** The widths the header cells of this row are drawn at right now, by column id. */
export function headerWidths(row: Element | null): Record<string, number> {
  const measured: Record<string, number> = {};
  row?.querySelectorAll<HTMLElement>("th[data-column]").forEach((th) => {
    if (th.dataset.column) measured[th.dataset.column] = th.getBoundingClientRect().width;
  });
  return measured;
}

/**
 * The width a column needs to show every cell drawn in it without cutting anything off:
 * its header cell `th` and its cell in each row on screen, each laid out on one line at
 * its natural width. Null when there is nothing to measure.
 *
 * Each cell gets a hidden copy of its content, sized to that content, inside the cell
 * itself — so the table's own styles still apply — and all copies are measured in one go,
 * so a page of rows costs one layout rather than one per row.
 */
export function fittedColumnWidth(th: HTMLTableCellElement): number | null {
  const table = th.closest("table");
  const headerRow = th.parentElement;
  if (!table || !(headerRow instanceof HTMLTableRowElement)) return null;

  const cells: HTMLTableCellElement[] = [th];
  for (const body of Array.from(table.tBodies)) {
    for (const row of Array.from(body.rows)) {
      // A row across the whole table — a group heading, "nothing found" — has no cell here.
      if (row.cells.length !== headerRow.cells.length) continue;
      const cell = row.cells[th.cellIndex];
      if (cell && cell.colSpan === 1) cells.push(cell);
    }
  }

  const probes = cells.map((cell) => {
    const probe = document.createElement("div");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText =
      "position:absolute;left:0;top:0;visibility:hidden;pointer-events:none;width:max-content;max-width:none;white-space:nowrap;";
    for (const child of Array.from(cell.childNodes)) {
      // The resize edge, and anything else laid over the cell, takes no room of its own.
      if (child instanceof HTMLElement && getComputedStyle(child).position === "absolute") continue;
      probe.appendChild(child.cloneNode(true));
    }
    cell.appendChild(probe);
    return { cell, probe };
  });

  let widest = 0;
  for (const { cell, probe } of probes) {
    const style = getComputedStyle(cell);
    const frame =
      parseFloat(style.paddingLeft) +
      parseFloat(style.paddingRight) +
      parseFloat(style.borderLeftWidth) +
      parseFloat(style.borderRightWidth);
    widest = Math.max(widest, probe.getBoundingClientRect().width + frame);
  }
  for (const { probe } of probes) probe.remove();

  // A pixel to spare: widths land on fractions, and a hair too narrow is an ellipsis.
  return widest > 0 ? Math.ceil(widest) + 1 : null;
}
